import React, { useState } from 'react'
import Image from 'next/image'
import * as Tabs from '@radix-ui/react-tabs'
import { Check, Copy, Server } from 'lucide-react'

import { toast } from '~/design-system/atom/use-toast'
import { useRouterParsed } from '~/hooks/utils/useRouterParse'

interface URLPreviewProps {
  mcpUrl: string
  onCopy?: (url: string, type: string) => void
}

// Helper function to remove transport=sse from URL
function removeTransportSSE(url: string): string {
  try {
    const urlObj = new URL(url)
    urlObj.searchParams.delete('transport')
    return urlObj.toString()
  } catch {
    // If URL parsing fails, try simple string replacement
    return url.replace(/[?&]transport=sse(&|$)/, (match, followingChar) => {
      return followingChar === '&' ? (match.startsWith('?') ? '?' : '&') : ''
    })
  }
}

const URLPreview: React.FC<URLPreviewProps> = ({ mcpUrl, onCopy }) => {
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null)
  const [selectedTab, setSelectedTab] = useState('cursor')
  const { connectedAccountId } = useRouterParsed()

  // Helper function to add connected_account_id query param
  const addConnectedAccountId = (url: string): string => {
    if (!connectedAccountId) return url

    // Check if this is a command string (contains npx and quoted URL)
    const commandMatch = url.match(
      /^(npx @composio\/mcp@latest setup ")([^"]+)(" --client .+)$/,
    )

    if (commandMatch && commandMatch[1] && commandMatch[2] && commandMatch[3]) {
      // Extract the URL from inside the quotes
      const prefix = commandMatch[1]
      const quotedUrl = commandMatch[2]
      const suffix = commandMatch[3]

      // Add the parameter to just the URL
      try {
        const urlObj = new URL(quotedUrl)
        urlObj.searchParams.set('connected_account_id', connectedAccountId)
        return `${prefix}${urlObj.toString()}${suffix}`
      } catch {
        // If URL parsing fails, try simple string append to the quoted URL
        const separator = quotedUrl.includes('?') ? '&' : '?'
        return `${prefix}${quotedUrl}${separator}connected_account_id=${connectedAccountId}${suffix}`
      }
    }

    // For non-command URLs (like SSE and HTTP endpoints), add parameter normally
    try {
      const urlObj = new URL(url)
      urlObj.searchParams.set('connected_account_id', connectedAccountId)
      return urlObj.toString()
    } catch {
      // If URL parsing fails, try simple string append
      const separator = url.includes('?') ? '&' : '?'
      return `${url}${separator}connected_account_id=${connectedAccountId}`
    }
  }

  const handleCopyUrl = async (url: string, type: string) => {
    try {
      await navigator.clipboard.writeText(url)
      setCopiedUrl(url)
      toast({
        title: 'Copied!',
        description: `${type} URL copied to clipboard`,
      })

      if (onCopy) {
        onCopy(url, type)
      }

      setTimeout(() => {
        setCopiedUrl(null)
      }, 2000)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy URL',
        variant: 'destructive',
      })
    }
  }

  const clientTabs = [
    {
      id: 'cursor',
      label: 'Cursor',
      logo: 'https://static.cdnlogo.com/logos/c/23/cursor.svg',
      description: 'Use with Cursor IDE',
      getUrl: () => {
        const cleanUrl = removeTransportSSE(mcpUrl)
        const urlWithMcp = cleanUrl.endsWith('/mcp')
          ? cleanUrl
          : cleanUrl + '/mcp'
        return `npx @composio/mcp@latest setup "${urlWithMcp}" --client cursor`
      },
      setupInstructions:
        'Run this command in your terminal to set up Cursor with MCP.',
    },
    {
      id: 'claude',
      label: 'Claude',
      logo: 'https://uxwing.com/wp-content/themes/uxwing/download/brands-and-social-media/claude-ai-icon.svg',
      description: 'Use with Claude Desktop',
      getUrl: () => {
        const cleanUrl = removeTransportSSE(mcpUrl)
        const urlWithMcp = cleanUrl.endsWith('/mcp')
          ? cleanUrl
          : cleanUrl + '/mcp'
        return `npx @composio/mcp@latest setup "${urlWithMcp}" --client claude`
      },
      setupInstructions:
        'Run this command in your terminal to set up Claude with MCP.',
    },
    {
      id: 'windsurf',
      label: 'Windsurf',
      logo: 'https://mcp.composio.dev/windsurf.svg',
      description: 'Use with Windsurf IDE',
      getUrl: () => {
        const cleanUrl = removeTransportSSE(mcpUrl)
        const urlWithMcp = cleanUrl.endsWith('/mcp')
          ? cleanUrl
          : cleanUrl + '/mcp'
        return `npx @composio/mcp@latest setup "${urlWithMcp}" --client windsurf`
      },
      setupInstructions:
        'Run this command in your terminal to set up Windsurf with MCP.',
    },
    {
      id: 'http-stream',
      label: 'HTTP',
      icon: Server,
      description: 'HTTP streaming endpoint',
      getUrl: () => {
        const cleanUrl = removeTransportSSE(mcpUrl)
        try {
          const urlObj = new URL(cleanUrl)
          if (!urlObj.pathname.endsWith('/mcp')) {
            urlObj.pathname = urlObj.pathname + '/mcp'
          }
          return urlObj.toString()
        } catch {
          return cleanUrl + '/mcp'
        }
      },
      setupInstructions:
        'Use this HTTP streaming endpoint with any HTTP-compatible client.',
    },
  ]

  return (
    <div className="w-full">
      <div className="rounded-lg bg-white">
        <div className="border-b p-4">
          <h3 className="text-lg font-semibold text-gray-900">
            MCP URL Compatibility
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            Your MCP server URL is compatible with multiple clients and
            protocols
          </p>
        </div>

        <Tabs.Root
          value={selectedTab}
          onValueChange={setSelectedTab}
          className="w-full"
        >
          <Tabs.List className="grid grid-cols-5 justify-items-start gap-1 overflow-x-auto border-b border-gray-200 px-4">
            {clientTabs.map((tab) => (
              <Tabs.Trigger
                key={tab.id}
                value={tab.id}
                className="flex w-full items-center justify-center gap-2 whitespace-nowrap border-b-2 border-transparent px-3 py-3 text-center text-sm font-medium text-gray-500 transition-colors hover:text-gray-700 data-[state=active]:border-blue-500 data-[state=active]:text-blue-600"
              >
                <div className="flex w-full items-center justify-center space-x-2">
                  {tab.logo ? (
                    <Image
                      src={tab.logo}
                      alt={`${tab.label} logo`}
                      width={16}
                      height={16}
                      className="object-contain"
                    />
                  ) : tab.icon ? (
                    <tab.icon className="h-4 w-4" />
                  ) : null}
                  <span className="inline-block text-sm">{tab.label}</span>
                </div>
              </Tabs.Trigger>
            ))}
          </Tabs.List>

          <div className="p-6">
            {clientTabs.map((tab) => (
              <Tabs.Content key={tab.id} value={tab.id} className="space-y-4">
                <div>
                  <h4 className="mb-2 font-medium text-gray-900">
                    {tab.description}
                  </h4>
                  <p className="mb-4 text-sm text-gray-600">
                    {tab.setupInstructions}
                  </p>
                </div>

                <div className="rounded-lg border bg-gray-50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <code className="flex-1 break-words font-mono text-sm text-gray-800">
                      {addConnectedAccountId(tab.getUrl())}
                    </code>
                    <button
                      onClick={() =>
                        void handleCopyUrl(
                          addConnectedAccountId(tab.getUrl()),
                          tab.label,
                        )
                      }
                      className="flex-shrink-0 rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                      title={`Copy ${tab.label} URL`}
                    >
                      {copiedUrl === addConnectedAccountId(tab.getUrl()) ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                {(tab.id === 'cursor' ||
                  tab.id === 'claude' ||
                  tab.id === 'windsurf') && (
                  <div className="rounded-md bg-blue-50 p-3 text-xs text-gray-500">
                    <strong>Note:</strong> After running the command, restart{' '}
                    {tab.label} to start using the MCP integration.
                  </div>
                )}

                {tab.id === 'sse' && (
                  <div className="rounded-md bg-purple-50 p-3 text-xs text-gray-500">
                    <strong>SSE Endpoint:</strong> This endpoint provides
                    real-time streaming capabilities for your MCP server.
                  </div>
                )}

                {tab.id === 'http-stream' && (
                  <div className="rounded-md bg-green-50 p-3 text-xs text-gray-500">
                    <strong>HTTP Endpoint:</strong> This is a standard HTTP
                    streaming endpoint compatible with any HTTP client.
                  </div>
                )}
              </Tabs.Content>
            ))}
          </div>
        </Tabs.Root>
      </div>
    </div>
  )
}

export default URLPreview
